import React, { useState, useEffect } from 'react';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://formspree.io/f/xkgbbwol', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject || 'Contact from Portfolio',
          message: formData.message,
        }),
      });

      if (response.ok) {
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        alert('Thank you! Your message has been sent successfully.');
        onClose();
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert(
        'Sorry, there was an error sending your message. Please try again or contact me directly at muhammad2004sarim@gmail.com',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = e => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  const inputClass =
    'w-full bg-navy border border-lightest-navy rounded px-4 py-3 text-lightest-slate text-[var(--fz-md)] font-sans transition-all placeholder:text-slate focus:outline-none focus:border-accent focus:shadow-[0_0_0_2px_var(--green-tint)]';

  return (
    /* Overlay */
    <div
      role="button"
      tabIndex={0}
      className="fixed inset-0 bg-[rgba(10,25,47,0.9)] flex items-center justify-center z-[9999] p-5"
      onClick={handleOverlayClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') handleOverlayClick(e);
      }}>

      {/* Modal card */}
      <div className="bg-light-navy rounded-custom p-10 w-full max-w-[500px] max-h-[90vh] overflow-y-auto shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] border border-lightest-navy relative">

        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-[15px] right-[15px] bg-none border-none text-slate text-[24px] cursor-pointer p-[5px] rounded w-8 h-8 flex items-center justify-center transition-all hover:text-lightest-slate hover:bg-[var(--green-tint)] focus:outline-none focus:text-accent focus:bg-[var(--green-tint)]">
          ×
        </button>

        {/* Header */}
        <div className="text-center mb-[30px]">
          <h2 className="text-lightest-slate text-[clamp(24px,5vw,32px)] m-0 font-semibold">
            Let&apos;s Develop Together!
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">

          <div className="flex flex-col">
            <label htmlFor="name" className="text-light-slate text-[var(--fz-sm)] mb-2 font-mono">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className={inputClass}
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="email" className="text-light-slate text-[var(--fz-sm)] mb-2 font-mono">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={inputClass}
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="phone" className="text-light-slate text-[var(--fz-sm)] mb-2 font-mono">
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={inputClass}
            />
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="subject"
              className="text-light-slate text-[var(--fz-sm)] mb-2 font-mono">
              Subject
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={inputClass}
            />
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="message"
              className="text-light-slate text-[var(--fz-sm)] mb-2 font-mono">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              className={`${inputClass} min-h-[120px] resize-y`}
            />
          </div>

          <div className="flex justify-center mt-5">
            <button
              type="submit"
              disabled={isSubmitting}
              className="resume-button flex items-center justify-center font-medium disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
              style={{ padding: '0.75rem 2.5rem' }}>
              {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
